import { Button, Grid, Typography } from "@mui/material";
import { Circle } from "@mui/icons-material";
import { observer } from "mobx-react";
import React from "react";
import { ConversationCommentActionNotificationArgument } from "stores/Notifications/Models/ConversationCommentNotificationArgument";
import theme from "Theme/AppTheme";
import { parseFromISO, standardDateTimeFormat } from "utils/DateTimeUtils";
import { useNavigate } from "react-router";
import { NotificationType } from "./MessageStoreBase";

interface Props {
    notification: ConversationCommentActionNotificationArgument;
    appPath: string;
}

const ConversationCommentNoticiation: React.FC<React.PropsWithChildren<Props>> =
    observer((props) => {
        const navigate = useNavigate();

        function notificationNavigateCallback() {
            props.notification.read = true;
            navigate(props.appPath);
        }

        return (
            <Button
                disableRipple
                sx={{
                    all: "unset",
                    display: "block",
                    cursor: "pointer",
                }}
                onClick={notificationNavigateCallback}
            >
                <Grid
                    container
                    xs={12}
                    key={"notification-item-" + props.notification.id}
                    sx={{
                        backgroundColor: props.notification.read
                            ? theme.palette.white.main
                            : theme.palette.primary[50],
                        "&:hover": {
                            backgroundColor: "#F5FAFF",
                        },
                        paddingLeft: theme.spacing(1),
                        paddingRight: theme.spacing(1),
                    }}
                >
                    <Grid container item xs={1}>
                        <Grid
                            item
                            sx={{
                                position: "relative",
                                display: "inline-flex",
                            }}
                        >
                            <Circle
                                sx={{
                                    color: theme.palette.primary[100],
                                    marginTop: "6px",
                                }}
                                fontSize={"large"}
                            />
                            <Typography
                                sx={{
                                    position: "absolute",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    color: theme.palette.primary[500],
                                    fontWeight: "bold",
                                    fontSize: 16,
                                    pointerEvents: "none",
                                    top: "12px",
                                }}
                            >
                                {props.notification.iconInitial}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container item xs={11}>
                        <Grid
                            item
                            xs={11}
                            zeroMinWidth
                            sx={{
                                paddingLeft: "16px",
                                paddingTop: theme.spacing(0.75),
                            }}
                        >
                            <Typography
                                sx={{
                                    whiteSpace: "normal",
                                    wordWrap: "break-word",
                                    overflowWrap: "break-word",
                                }}
                            >
                                <strong>{props.notification.fullName}</strong>{" "}
                                {props.notification.titleMessage}
                            </Typography>
                        </Grid>
                    </Grid>
                    {props.notification.type === NotificationType.Comment && (
                        <Grid
                            item
                            xs={12}
                            sx={{
                                paddingLeft: theme.spacing(0.75),
                                paddingTop: theme.spacing(0.5),
                            }}
                            zeroMinWidth
                        >
                            <Grid
                                style={{
                                    margin: "0.25rem",
                                    padding: theme.spacing(1),
                                    border: "1px solid",
                                    borderColor:
                                        theme.palette.lightgrayBorder.main,
                                    backgroundColor: theme.palette.white.main,
                                }}
                            >
                                <Grid
                                    container
                                    item
                                    xs={12}
                                    style={{
                                        marginBottom: "5px",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            overflowWrap: "break-word",
                                            whiteSpace: "pre-wrap",
                                            display: "-webkit-box",
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {props.notification.message}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                    <Grid
                        item
                        xs={12}
                        container
                        justifyContent={"left"}
                        sx={{
                            marginTop: theme.spacing(1),
                            marginBottom: theme.spacing(1),
                        }}
                    >
                        <Typography
                            sx={{
                                paddingLeft: "16px",
                                fontSize: "13px",
                                overflowWrap: "break-word",
                                whiteSpace: "pre-wrap",
                            }}
                        >
                            {standardDateTimeFormat(
                                parseFromISO(props.notification.timestamp),
                                "MM-DD-YYYY, hh:mm A",
                            )}
                        </Typography>
                    </Grid>
                </Grid>
            </Button>
        );
    });

export default ConversationCommentNoticiation;
